import { useMemo } from 'react';

// QR version 10-L holds up to 271 UTF-8 bytes and needs no external service.
const version = 10;
const size = 4 * version + 17;
const dataCodewords = 274;
const errorCodewordsPerBlock = 18;
const blockCount = 4;

function multiply(x, y) {
  let result = 0;
  for (let bit = 7; bit >= 0; bit -= 1) {
    result = (result << 1) ^ ((result >>> 7) * 0x11d);
    result ^= ((y >>> bit) & 1) * x;
  }
  return result;
}

function errorCorrection(data) {
  const divisor = Array(errorCodewordsPerBlock).fill(0);
  divisor[errorCodewordsPerBlock - 1] = 1;
  let root = 1;
  for (let i = 0; i < errorCodewordsPerBlock; i += 1) {
    for (let j = 0; j < divisor.length; j += 1) {
      divisor[j] = multiply(divisor[j], root);
      if (j + 1 < divisor.length) divisor[j] ^= divisor[j + 1];
    }
    root = multiply(root, 2);
  }

  const remainder = Array(errorCodewordsPerBlock).fill(0);
  for (const byte of data) {
    const factor = byte ^ remainder.shift();
    remainder.push(0);
    for (let i = 0; i < remainder.length; i += 1) {
      remainder[i] ^= multiply(divisor[i], factor);
    }
  }
  return remainder;
}

function encodeData(value) {
  // The otpauth URI is ASCII: the account label is percent-encoded.
  const bytes = Array.from(value, (character) => character.charCodeAt(0));
  if (bytes.some((byte) => byte > 127) || bytes.length > 271) return null;

  const bits = [];
  const append = (number, length) => {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((number >>> i) & 1);
  };
  append(4, 4); // Byte mode
  append(bytes.length, 16); // Version 10 uses a 16-bit byte count
  bytes.forEach((byte) => append(byte, 8));
  append(0, Math.min(4, dataCodewords * 8 - bits.length));
  while (bits.length % 8 !== 0) bits.push(0);

  const data = [];
  for (let i = 0; i < bits.length; i += 8) {
    data.push(bits.slice(i, i + 8).reduce((result, bit) => (result << 1) | bit, 0));
  }
  let pad = 0xec;
  while (data.length < dataCodewords) {
    data.push(pad);
    pad = pad === 0xec ? 0x11 : 0xec;
  }

  const shortBlockLength = 86; // Version 10-L has 346 raw codewords in four blocks.
  const shortBlockCount = 2;
  const blocks = [];
  let offset = 0;
  for (let i = 0; i < blockCount; i += 1) {
    const length = shortBlockLength - errorCodewordsPerBlock + (i < shortBlockCount ? 0 : 1);
    const block = data.slice(offset, offset + length);
    offset += length;
    const correction = errorCorrection(block);
    if (i < shortBlockCount) block.push(0); // Interleaving placeholder
    blocks.push(block.concat(correction));
  }

  const result = [];
  for (let column = 0; column < blocks[0].length; column += 1) {
    for (let block = 0; block < blockCount; block += 1) {
      if (column !== shortBlockLength - errorCodewordsPerBlock || block >= shortBlockCount) {
        result.push(blocks[block][column]);
      }
    }
  }
  return result;
}

export function createTotpQrMatrix(value) {
  const codewords = encodeData(value);
  if (!codewords) return null;

  const modules = Array.from({ length: size }, () => Array(size).fill(false));
  const reserved = Array.from({ length: size }, () => Array(size).fill(false));
  const set = (x, y, dark) => {
    if (x >= 0 && x < size && y >= 0 && y < size) {
      modules[y][x] = dark;
      reserved[y][x] = true;
    }
  };

  for (let i = 0; i < size; i += 1) {
    set(6, i, i % 2 === 0);
    set(i, 6, i % 2 === 0);
  }
  for (const [centerX, centerY] of [[3, 3], [size - 4, 3], [3, size - 4]]) {
    for (let dy = -4; dy <= 4; dy += 1) {
      for (let dx = -4; dx <= 4; dx += 1) {
        const distance = Math.max(Math.abs(dx), Math.abs(dy));
        set(centerX + dx, centerY + dy, distance !== 2 && distance !== 4);
      }
    }
  }
  // Alignment patterns on row/column 6 replace timing modules; only finder overlaps are skipped.
  for (const centerY of [6, 28, 50]) {
    for (const centerX of [6, 28, 50]) {
      if ((centerX === 6 && centerY === 6) ||
          (centerX === 6 && centerY === 50) ||
          (centerX === 50 && centerY === 6)) continue;
      for (let dy = -2; dy <= 2; dy += 1) {
        for (let dx = -2; dx <= 2; dx += 1) {
          set(centerX + dx, centerY + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
        }
      }
    }
  }

  let formatRemainder = 8; // Error correction level L (01), mask pattern 0.
  for (let i = 0; i < 10; i += 1) {
    formatRemainder = (formatRemainder << 1) ^ ((formatRemainder >>> 9) * 0x537);
  }
  const format = ((8 << 10) | formatRemainder) ^ 0x5412;
  const formatBit = (bit) => ((format >>> bit) & 1) !== 0;
  for (let i = 0; i <= 5; i += 1) set(8, i, formatBit(i));
  set(8, 7, formatBit(6));
  set(8, 8, formatBit(7));
  set(7, 8, formatBit(8));
  for (let i = 9; i < 15; i += 1) set(14 - i, 8, formatBit(i));
  for (let i = 0; i < 8; i += 1) set(size - 1 - i, 8, formatBit(i));
  for (let i = 8; i < 15; i += 1) set(8, size - 15 + i, formatBit(i));
  set(8, size - 8, true);

  let versionRemainder = version;
  for (let i = 0; i < 12; i += 1) {
    versionRemainder = (versionRemainder << 1) ^ ((versionRemainder >>> 11) * 0x1f25);
  }
  const versionBits = (version << 12) | versionRemainder;
  for (let i = 0; i < 18; i += 1) {
    const dark = ((versionBits >>> i) & 1) !== 0;
    const a = size - 11 + i % 3;
    const b = Math.floor(i / 3);
    set(a, b, dark);
    set(b, a, dark);
  }

  let bitIndex = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let row = 0; row < size; row += 1) {
      const y = ((right + 1) & 2) === 0 ? size - 1 - row : row;
      for (let dx = 0; dx < 2; dx += 1) {
        const x = right - dx;
        if (!reserved[y][x] && bitIndex < codewords.length * 8) {
          modules[y][x] = ((codewords[bitIndex >>> 3] >>> (7 - (bitIndex & 7))) & 1) !== 0;
          bitIndex += 1;
        }
      }
    }
  }

  // Fixed mask 0 is valid; apply it only to data modules.
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      if (!reserved[y][x] && (x + y) % 2 === 0) modules[y][x] = !modules[y][x];
    }
  }
  return modules;
}

export default function TotpQrCode({ value, label = '認証アプリ設定QRコード' }) {
  const matrix = useMemo(() => createTotpQrMatrix(value), [value]);
  if (!matrix) return null;

  const path = [];
  matrix.forEach((row, y) => row.forEach((dark, x) => {
    if (dark) path.push(`M${x + 4} ${y + 4}h1v1h-1z`);
  }));
  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${size + 8} ${size + 8}`}
      width="260"
      height="260"
      className="mx-auto max-w-full"
      shapeRendering="crispEdges"
    >
      <rect width={size + 8} height={size + 8} fill="#fff" />
      <path d={path.join('')} fill="#000" />
    </svg>
  );
}
